import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { residentialUnitReceiptAPI } from '../../services/api';
import SuccessModal from '../Common/SuccessModal';
import ErrorModal from '../Common/ErrorModal';
import './ResidentialUnitReceiptCreate.css';

const ResidentialUnitReceiptEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    contractNumber: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    propertyName: '',
    unitNumber: '',
    firstPartyName: '',
    firstPartyIdNumber: '',
    firstPartyIdType: '',
    firstPartyStatus: '',
    firstPartyReceiptStatus: '',
    firstPartyNotes: '',
    secondPartyName: '',
    secondPartyIdNumber: '',
    secondPartyIdType: '',
    secondPartyStatus: '',
    secondPartyReceiptStatus: '',
    isActive: 'true',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadResidentialUnitReceipt();
  }, [id]);

  const loadResidentialUnitReceipt = async () => {
    try {
      setLoadingData(true);
      const response = await residentialUnitReceiptAPI.getById(id);
      const data = response.data;
      setFormData({
        ...data,
        startDate: data.startDate ? new Date(data.startDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        endDate: data.endDate ? new Date(data.endDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        isActive: data.isActive ? 'true' : 'false',
      });
    } catch (err) {
      setErrorMessage(t('residentialUnitReceipt.edit.messages.loadError'));
      setShowErrorModal(true);
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.contractNumber.trim()) {
      newErrors.contractNumber = t('residentialUnitReceipt.edit.errors.contractNumberRequired');
    }
    if (!formData.startDate) {
      newErrors.startDate = t('residentialUnitReceipt.edit.errors.startDateRequired');
    }
    if (!formData.endDate) {
      newErrors.endDate = t('residentialUnitReceipt.edit.errors.endDateRequired');
    }
    if (!formData.propertyName.trim()) {
      newErrors.propertyName = t('residentialUnitReceipt.edit.errors.propertyNameRequired');
    }
    if (!formData.unitNumber.trim()) {
      newErrors.unitNumber = t('residentialUnitReceipt.edit.errors.unitNumberRequired');
    }
    if (!formData.firstPartyName.trim()) {
      newErrors.firstPartyName = t('residentialUnitReceipt.edit.errors.firstPartyNameRequired');
    }
    if (!formData.firstPartyIdNumber.trim()) {
      newErrors.firstPartyIdNumber = t('residentialUnitReceipt.edit.errors.firstPartyIdNumberRequired');
    }
    if (!formData.secondPartyName.trim()) {
      newErrors.secondPartyName = t('residentialUnitReceipt.edit.errors.secondPartyNameRequired');
    }
    if (!formData.secondPartyIdNumber.trim()) {
      newErrors.secondPartyIdNumber = t('residentialUnitReceipt.edit.errors.secondPartyIdNumberRequired');
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      newErrors.endDate = t('residentialUnitReceipt.edit.errors.endDateBeforeStartDate');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        isActive: formData.isActive === 'true',
      };
      await residentialUnitReceiptAPI.update(id, submitData);
      setShowSuccessModal(true);
      setTimeout(() => {
        navigate('/residential-unit-receipt');
      }, 1000);
    } catch (err) {
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else {
        setErrorMessage(err.response?.data?.message || t('residentialUnitReceipt.edit.messages.updateError'));
        setShowErrorModal(true);
      }
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Unit Information */}
          <h3 className="form-section-title">{t('residentialUnitReceipt.edit.sections.unitInformation')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-file-contract"></i> {t('residentialUnitReceipt.edit.fields.contractNumber')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-file-contract input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.contractNumber ? 'is-invalid' : ''}`}
                  name="contractNumber"
                  value={formData.contractNumber}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.contractNumber')}
                  maxLength={50}
                />
              </div>
              {errors.contractNumber && (
                <div className="error-message">{errors.contractNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('residentialUnitReceipt.edit.fields.startDate')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.startDate ? 'is-invalid' : ''}`}
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
              {errors.startDate && (
                <div className="error-message">{errors.startDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-calendar"></i> {t('residentialUnitReceipt.edit.fields.endDate')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-calendar input-icon"></i>
                <input
                  type="date"
                  className={`form-input ${errors.endDate ? 'is-invalid' : ''}`}
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
              {errors.endDate && (
                <div className="error-message">{errors.endDate}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-building"></i> {t('residentialUnitReceipt.edit.fields.propertyName')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-building input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.propertyName ? 'is-invalid' : ''}`}
                  name="propertyName"
                  value={formData.propertyName}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.propertyName')}
                  maxLength={255}
                />
              </div>
              {errors.propertyName && (
                <div className="error-message">{errors.propertyName}</div>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-home"></i> {t('residentialUnitReceipt.edit.fields.unitNumber')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-home input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.unitNumber ? 'is-invalid' : ''}`}
                  name="unitNumber"
                  value={formData.unitNumber}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.unitNumber')}
                  maxLength={50}
                />
              </div>
              {errors.unitNumber && (
                <div className="error-message">{errors.unitNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('residentialUnitReceipt.edit.fields.isActive')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <select
                  className="form-input"
                  name="isActive"
                  value={formData.isActive}
                  onChange={handleChange}
                >
                  <option value="true">{t('common.yes')}</option>
                  <option value="false">{t('common.no')}</option>
                </select>
              </div>
            </div>
          </div>

          {/* First Party (Lessor) */}
          <h3 className="form-section-title">{t('residentialUnitReceipt.edit.sections.firstParty')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('residentialUnitReceipt.edit.fields.firstPartyName')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.firstPartyName ? 'is-invalid' : ''}`}
                  name="firstPartyName"
                  value={formData.firstPartyName}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.firstPartyName')}
                  maxLength={255}
                />
              </div>
              {errors.firstPartyName && (
                <div className="error-message">{errors.firstPartyName}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-card"></i> {t('residentialUnitReceipt.edit.fields.firstPartyIdNumber')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-card input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.firstPartyIdNumber ? 'is-invalid' : ''}`}
                  name="firstPartyIdNumber"
                  value={formData.firstPartyIdNumber}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.firstPartyIdNumber')}
                  maxLength={50}
                />
              </div>
              {errors.firstPartyIdNumber && (
                <div className="error-message">{errors.firstPartyIdNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-badge"></i> {t('residentialUnitReceipt.edit.fields.firstPartyIdType')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-badge input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyIdType"
                  value={formData.firstPartyIdType}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.firstPartyIdType')}
                  maxLength={50}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user-tag"></i> {t('residentialUnitReceipt.edit.fields.firstPartyStatus')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user-tag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyStatus"
                  value={formData.firstPartyStatus}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.firstPartyStatus')}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('residentialUnitReceipt.edit.fields.firstPartyReceiptStatus')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyReceiptStatus"
                  value={formData.firstPartyReceiptStatus}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.firstPartyReceiptStatus')}
                  maxLength={50}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-sticky-note"></i> {t('residentialUnitReceipt.edit.fields.firstPartyNotes')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-sticky-note input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="firstPartyNotes"
                  value={formData.firstPartyNotes}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.firstPartyNotes')}
                  maxLength={1000}
                />
              </div>
            </div>
          </div>

          {/* Second Party (Lessee) */}
          <h3 className="form-section-title">{t('residentialUnitReceipt.edit.sections.secondParty')}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user"></i> {t('residentialUnitReceipt.edit.fields.secondPartyName')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.secondPartyName ? 'is-invalid' : ''}`}
                  name="secondPartyName"
                  value={formData.secondPartyName}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.secondPartyName')}
                  maxLength={255}
                />
              </div>
              {errors.secondPartyName && (
                <div className="error-message">{errors.secondPartyName}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-card"></i> {t('residentialUnitReceipt.edit.fields.secondPartyIdNumber')} *
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-card input-icon"></i>
                <input
                  type="text"
                  className={`form-input ${errors.secondPartyIdNumber ? 'is-invalid' : ''}`}
                  name="secondPartyIdNumber"
                  value={formData.secondPartyIdNumber}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.secondPartyIdNumber')}
                  maxLength={50}
                />
              </div>
              {errors.secondPartyIdNumber && (
                <div className="error-message">{errors.secondPartyIdNumber}</div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-id-badge"></i> {t('residentialUnitReceipt.edit.fields.secondPartyIdType')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-id-badge input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="secondPartyIdType"
                  value={formData.secondPartyIdType}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.secondPartyIdType')}
                  maxLength={50}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-user-tag"></i> {t('residentialUnitReceipt.edit.fields.secondPartyStatus')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-user-tag input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="secondPartyStatus"
                  value={formData.secondPartyStatus}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.secondPartyStatus')}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                <i className="fas fa-check-circle"></i> {t('residentialUnitReceipt.edit.fields.secondPartyReceiptStatus')}
              </label>
              <div className="input-wrapper">
                <i className="fas fa-check-circle input-icon"></i>
                <input
                  type="text"
                  className="form-input"
                  name="secondPartyReceiptStatus"
                  value={formData.secondPartyReceiptStatus}
                  onChange={handleChange}
                  placeholder={t('residentialUnitReceipt.edit.fields.secondPartyReceiptStatus')}
                  maxLength={50}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={() => navigate('/residential-unit-receipt')}
            >
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? t('common.loading') : t('residentialUnitReceipt.edit.save')}
            </button>
          </div>
        </form>
      </div>

      <SuccessModal
        show={showSuccessModal}
        message={t('residentialUnitReceipt.edit.messages.updateSuccess')}
        onClose={() => {
          setShowSuccessModal(false);
        }}
      />

      <ErrorModal
        show={showErrorModal}
        message={errorMessage}
        onClose={() => setShowErrorModal(false)}
      />
    </div>
  );
};

export default ResidentialUnitReceiptEdit;
